import React, { useEffect, useMemo, useState } from 'react';
import { useWorld } from '../../contexts/WorldDataContext';
import { WorldPageHeader, WorldSection } from '../../components/world/WorldPageHeader';
import { NpcShopCard } from '../../components/world/NpcShopCard';
import { useHashScroll } from '../../hooks/useHashScroll';
import { npcs } from '../../data/guide/npcs';
import { npcShopPendings } from '../../data/guide/shops';
import { resolveNpcLocation } from '../../utils/npcLocation';
import { compareMapGroups, locationPoints, serviceLabel, UNLOCATED } from '../../utils/npcShops';
import { Link } from 'react-router-dom';
import { UserRound } from 'lucide-react';
import { EntityThumb } from '../../components/world/EntityThumb';
import { npcPhotos } from '../../data/guide/npcPhotoSheets';
import { monsterPath } from '../../utils/worldFormat';
import type { NpcServiceKind } from '../../types/shop';
const FILTERS: (NpcServiceKind | 'todos')[] = ['todos', 'venta', 'reparacion', 'misiones', 'combinaciones', 'acceso'];

/** "NPC y tiendas": NPC por mapa (Lorencia y Noria primero), con ubicación, servicios e inventario. */
export function NpcShopsGuide() {
  const world = useWorld();
  const [service, setService] = useState<NpcServiceKind | 'todos'>('todos');
  useHashScroll();
  useEffect(() => {
    document.title = 'NPC y tiendas · Guía MU Vanguard';
  }, []);
  const groups = useMemo(() => {
    const snap = {
      status: world.status,
      data: world.data,
      error: world.error
    };
    const map = new Map<string, {
      npc: (typeof npcs)[number];
      loc: ReturnType<typeof resolveNpcLocation>;
    }[]>();
    npcs.forEach(npc => {
      if (service !== 'todos' && !npc.services?.some(s => s.kind === service)) return;
      const loc = resolveNpcLocation(npc, snap);
      const names = Array.from(new Set(locationPoints(loc).map(p => p.mapName)));
      // Un NPC que se desplaza entre varios mapas se muestra una sola vez, en el primero.
      const key = names[0] ?? UNLOCATED;
      map.set(key, [...(map.get(key) ?? []), {
        npc,
        loc
      }]);
    });
    return Array.from(map.entries()).sort(([a], [b]) => compareMapGroups(a, b));
  }, [world.status, world.data, world.error, service]);

  // NPC del paquete de fotos que aún no tienen ficha propia en la guía (solo si existen en el catálogo).
  const otherNpcs = useMemo(() => {
    const w = world.data;
    if (!w || service !== 'todos') return [];
    const guideIds = new Set(npcs.map(n => n.monsterId));
    return npcPhotos.filter(p => !guideIds.has(p.id)).map(p => w.monsters.get(p.id)).filter((m): m is NonNullable<typeof m> => !!m).sort((a, b) => a.name.localeCompare(b.name, 'es'));
  }, [world.data, service]);
  return <div className="mx-auto w-full max-w-5xl" data-id="element-2361" data-mp="2g7zheu0q125x">
      <WorldPageHeader crumbs={[{
      label: 'Guía',
      to: '/guia'
    }, {
      label: 'NPC y tiendas'
    }]} eyebrow="Ciudades" title="NPC y tiendas" data-id="element-2362" data-mp="2g8rds72573x9">
        <p className="mt-3 max-w-2xl text-sm md:text-base text-gray-300" data-id="element-2363" data-mp="21d1mbte6tyl5">
          Dónde está cada NPC, qué servicio presta y qué vende. Las funciones marcadas como clásicas son de MU Online Season 6 y están pendientes de validar en Vanguard.
        </p>
      </WorldPageHeader>

      <div role="group" aria-label="Filtrar por servicio" className="-mx-4 mb-6 flex gap-2 overflow-x-auto px-4 pb-1 md:mx-0 md:flex-wrap md:px-0" data-id="element-2364" data-mp="1io7x33wlz8xe">
        {FILTERS.map(f => <button key={f} type="button" aria-pressed={service === f} onClick={() => setService(f)} className={`h-9 shrink-0 rounded-full border px-3.5 text-sm font-semibold transition-colors duration-150 ${service === f ? 'border-[#D4AF37] bg-[#D4AF37]/15 text-white' : 'border-white/10 text-gray-300 hover:text-white'}`} data-id="element-2365" data-mp="1rmbjs3jbybqs">
            {f === 'todos' ? 'Todos' : serviceLabel[f]}
          </button>)}
      </div>

      <div className="space-y-10" data-id="element-2366" data-mp="2pbwosg4qm37y">
        {groups.length === 0 && <p className="text-gray-400" data-id="element-2367" data-mp="2kbqqih3ypigw">Ningún NPC presta este servicio según los datos disponibles.</p>}
        {groups.map(([mapName, list]) => <WorldSection key={mapName} id={`mapa-${mapName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} title={mapName} count={list.length} data-id="element-2368" data-mp="hobsm4jqvrl3">
            <div className="grid gap-6 md:grid-cols-2" data-id="element-2369" data-mp="mltu59vfn4j2">
              {list.map(({
            npc,
            loc
          }) => <NpcShopCard key={npc.id} npc={npc} loc={loc} w={world.data} data-id="element-2370" data-mp="rgzlwpbindf2" />)}
            </div>
          </WorldSection>)}

        {otherNpcs.length > 0 && <WorldSection id="otros-npc" title="Otros NPC identificados" count={otherNpcs.length} data-id="element-2371" data-mp="3uwwyf4x5gxgk">
            <p className="mb-4 max-w-2xl text-sm text-gray-400" data-id="element-2372" data-mp="3q3mp74qdfxs">Identificados por su ID con foto de referencia. Su función, ubicación y qué venden en Vanguard están pendientes de documentar.</p>
            <ul className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3" data-id="element-2373" data-mp="dojd17vxol68">
              {otherNpcs.map(m => <li key={m.id} className="flex items-center gap-3 rounded-2xl border border-white/10 p-3" data-id="element-2374" data-mp="1k32986z38705">
                  <EntityThumb entity={{
              type: 'monster',
              id: m.id
            }} name={m.name} size="card" fallback={<UserRound className="h-4 w-4 text-[#D4AF37]" aria-hidden="true" data-id="element-2376" data-mp="39jecmntqw2ew" />} data-id="element-2375" data-mp="1p0tzkwk33l28" />
                  <span className="min-w-0" data-id="element-2377" data-mp="34mnxq3tofcv2">
                    <Link to={monsterPath(m.id)} className="block truncate font-semibold text-white underline decoration-[#D4AF37]/50 underline-offset-4 hover:text-[#D4AF37]" data-id="element-2378" data-mp="34nyz5wzng8j0">{m.name}</Link>
                    <span className="block text-xs text-gray-500" data-id="element-2379" data-mp="2utp58av27266">Función no documentada</span>
                  </span>
                </li>)}
            </ul>
          </WorldSection>}

        <WorldSection id="datos-que-faltan" title="Datos que faltan" count={npcShopPendings.length} data-id="element-2380" data-mp="2lxhl8yjh7hhp">
          <ol className="space-y-3" data-id="element-2381" data-mp="2gxv62jk37r69">
            {npcShopPendings.map((p, i) => <li key={p.what} className="flex gap-3 border-t border-white/10 pt-3 first:border-0 first:pt-0" data-id="element-2382" data-mp="2bz1skr4sw063" data-mpi={i}>
                <span className="font-mono text-sm text-gray-500" data-id="element-2383" data-mp="f2b0m9okbsw8">{i + 1}</span>
                <span data-id="element-2384" data-mp="a6dji15h6iie">
                  <span className="block font-semibold text-white" data-id="element-2385" data-mp="24lssq4nxso66">{p.what}</span>
                  <span className="block text-sm text-gray-400" data-id="element-2386" data-mp="24jps6qp5rcn7">{p.detail}</span>
                </span>
              </li>)}
          </ol>
        </WorldSection>
      </div>
    </div>;
}